import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Flex, Form, Input, Modal, Steps } from "antd";
import Upload from "@/components/Upload";
import templateStore from "@/store/templateStore";
import { GetLocalStorage } from "@/utils/helper";
import PublicProfile from "@/page/PublicTemplates/Profile";
import type { ICusTemplate } from "@/store/interface/template";
import { validatePhoneNumber } from "@/utils/validate";

const FormProfile = ({ initialData }: { initialData?: ICusTemplate }) => {
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewConfig, setPreviewConfig] = useState<any>({});
  const { saveConfig, updateConfig } = templateStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (initialData) form.setFieldsValue(initialData.config);
  }, []);

  const handlePreview = () => {
    setPreviewConfig(form.getFieldsValue());
    setPreviewOpen(true);
  };

  const steps = [{ title: "Bước 1" }, { title: "Bước 2" }, { title: "Bước 3" }];

  const stepContent = [
    // Step 1 — Thông tin cá nhân
    <>
      <Form.Item name="avatar" label="Ảnh đại diện">
        <Upload maxSize={10} />
      </Form.Item>
      <Form.Item name="cover_image" label="Ảnh bìa">
        <Upload maxSize={10} />
      </Form.Item>
      <Form.Item name="full_name" label="Họ và tên" rules={[{ required: true, message: "Vui lòng nhập họ và tên" }]}>
        <Input placeholder="Nhập họ và tên" />
      </Form.Item>
      <Form.Item name="title" label="Chức danh / Nghề nghiệp">
        <Input placeholder="VD: UI/UX Designer, Kỹ sư phần mềm..." />
      </Form.Item>
      <Form.Item name="bio" label="Giới thiệu bản thân">
        <Input.TextArea placeholder="Viết vài dòng giới thiệu về bạn..." rows={4} />
      </Form.Item>
    </>,
    // Step 2 — Thông tin liên hệ & mạng xã hội
    <>
      <Form.Item
        name="phone"
        label="Số điện thoại"
        rules={[
          () => ({
            validator(_, value) {
              if (!value || validatePhoneNumber(value)) {
                return Promise.resolve();
              }
              return Promise.reject(new Error("Số điện thoại không hợp lệ"));
            },
          }),
        ]}
      >
        <Input placeholder="Nhập số điện thoại" />
      </Form.Item>
      <Form.Item name="email" label="Email">
        <Input placeholder="Nhập email" />
      </Form.Item>
      <Form.Item name="address" label="Địa chỉ">
        <Input placeholder="Nhập địa chỉ" />
      </Form.Item>
      <Form.Item name="website" label="Website" rules={[{ type: "url", message: "Đường dẫn không hợp lệ" }]}>
        <Input placeholder="https://..." />
      </Form.Item>
      <Form.Item name="facebook" label="Facebook" rules={[{ type: "url", message: "Đường dẫn không hợp lệ" }]}>
        <Input placeholder="Link Facebook" />
      </Form.Item>
      <Form.Item name="instagram" label="Instagram" rules={[{ type: "url", message: "Đường dẫn không hợp lệ" }]}>
        <Input placeholder="Link Instagram" />
      </Form.Item>
      <Form.Item name="linkedin" label="LinkedIn" rules={[{ type: "url", message: "Đường dẫn không hợp lệ" }]}>
        <Input placeholder="Link LinkedIn" />
      </Form.Item>
      <Form.Item name="tiktok" label="TikTok" rules={[{ type: "url", message: "Đường dẫn không hợp lệ" }]}>
        <Input placeholder="Link TikTok" />
      </Form.Item>
    </>,
    // Step 3 — Gallery ảnh & video giới thiệu
    <>
      <Form.Item name="intro_video" label="Video giới thiệu">
        <Upload type="video" maxSize={10} />
      </Form.Item>
      <Form.Item name="gallery" label="Bộ sưu tập ảnh">
        <Upload multiple maxSize={10} />
      </Form.Item>
    </>,
  ];

  const next = () => setCurrentStep(currentStep + 1);
  const prev = () => setCurrentStep(currentStep - 1);

  const handleFinish = async (value: any) => {
    try {
      let result;
      if (initialData) {
        result = await updateConfig(initialData.xid, { config: { ...value } });
      } else {
        const serial = await GetLocalStorage("scan-storage", "serial");
        result = await saveConfig({ type: "profile", chip_serial: serial, config: { ...value } });
      }
      if (result) navigate("/");
    } catch (error) {
      console.log("error", error);
    }
  };

  return (
    <section className="container">
      <Steps responsive={false} type="dot" titlePlacement="vertical" current={currentStep} items={steps} />

      <Form form={form} layout="vertical" style={{ marginTop: 24 }} onFinish={handleFinish} preserve>
        {stepContent.map((content, index) => (
          <div key={index} style={{ display: index === currentStep ? "block" : "none" }}>
            {content}
          </div>
        ))}

        <Flex className="mt-3">
          {currentStep > 0 && (
            <Button style={{ marginRight: 8 }} onClick={prev} className="w-full">
              Quay lại
            </Button>
          )}
          {currentStep < steps.length - 1 && (
            <Button type="primary" onClick={next} className="w-full">
              Tiếp theo
            </Button>
          )}
          {currentStep === steps.length - 1 && (
            <>
              <Button onClick={handlePreview} style={{ marginRight: 8 }} className="w-full">
                Xem trước
              </Button>
              <Button type="primary" htmlType="submit" className="w-full">
                Hoàn thành
              </Button>
            </>
          )}
        </Flex>
      </Form>

      <Modal
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        footer={null}
        title="Xem trước"
        width={480}
        style={{ top: 8 }}
        styles={{ body: { padding: 0, height: "calc(100vh - 110px)", overflow: "auto" } }}
      >
        <PublicProfile config={previewConfig} />
      </Modal>
    </section>
  );
};

export default FormProfile;
